import { Link } from 'react-router';
import { useElevenlabsAccount } from '../hooks/useApi';

/**
 * Página `/admin/elevenlabs-account` — vista admin del consumo TOTAL de la cuenta
 * ElevenLabs del workspace XLS, no solo lo que xVision usó.
 *
 * Útil porque la cuenta es compartida (otros sistemas/personas también consumen
 * el plan). Acá vemos cuánto se está usando entre todos vs cuánto fue xVision.
 */
export function ElevenlabsAccountPage() {
  const { data, isLoading, error } = useElevenlabsAccount();

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <header className="mb-6">
        <Link to="/costs" className="text-xs text-slate-400 hover:text-vision underline">
          ← volver a Dashboard de gastos
        </Link>
        <h1 className="text-2xl font-bold text-slate-100 mt-2">
          🟢 Cuenta ElevenLabs (workspace XLS)
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Consumo total del plan compartido. Útil para entender qué tan llena está la cuenta
          más allá de lo que generó xVision.
        </p>
      </header>

      {isLoading && <div className="text-slate-400">Consultando ElevenLabs…</div>}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 text-sm">
          {error instanceof Error ? error.message : String(error)}
          {error instanceof Error && error.message.includes('Forbidden') && (
            <div className="mt-2 text-xs text-red-200/70">
              Esta página requiere rol <code>admin</code>.
            </div>
          )}
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {/* Plan general */}
          <section className="bg-slate-900 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-slate-100">Plan vigente</h2>
              <span className="text-xs uppercase tracking-wide text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                {data.plan.tier}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <Stat label="Consumido (todos)" value={data.plan.characterCount.toLocaleString()} suffix="chars" />
              <Stat label="Disponible" value={data.plan.characterLimit.toLocaleString()} suffix="chars" />
              <Stat
                label="% del plan"
                value={data.plan.percentUsed.toFixed(1)}
                suffix="%"
                color={percentColor(data.plan.percentUsed)}
              />
            </div>

            <ProgressBar percent={data.plan.percentUsed} />

            <div className="mt-3 text-xs text-slate-400">
              Próximo reset:{' '}
              <span className="text-slate-200">
                {data.plan.nextResetIso
                  ? new Date(data.plan.nextResetIso).toLocaleString()
                  : 'desconocido'}
              </span>
            </div>
          </section>

          {/* Breakdown xVision vs externo */}
          <section className="bg-slate-900 border border-slate-700 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-slate-100 mb-3">¿Quién consumió?</h2>

            <div className="space-y-4">
              <BreakdownRow
                label="xVision (esta app)"
                chars={data.breakdown.xvision.chars}
                percent={data.breakdown.xvision.percent}
                color="bg-vision"
              />
              <BreakdownRow
                label="Externo (otros sistemas / personas)"
                chars={data.breakdown.external.chars}
                percent={data.breakdown.external.percent}
                color="bg-orange-500"
              />
            </div>

            <div className="mt-4 pt-4 border-t border-slate-800 text-xs text-slate-400">
              <strong className="text-slate-300">Cómo se calcula:</strong> el total del plan lo
              reporta ElevenLabs en vivo. La parte de xVision sale de nuestra tabla{' '}
              <code className="text-vision">elevenlabs_usage</code> (cada generación trackea
              caracteres). El resto se asume "externo" — no podemos discriminar qué sistema lo
              consumió porque la API key es compartida.
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  suffix,
  color,
}: {
  label: string;
  value: string;
  suffix?: string;
  color?: string;
}) {
  return (
    <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${color ?? 'text-slate-100'}`}>
        {value}
        {suffix && <span className="text-sm text-slate-400 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  const capped = Math.min(percent, 100);
  const color = percent >= 100 ? 'bg-red-500' : percent >= 80 ? 'bg-yellow-500' : 'bg-emerald-500';
  return (
    <div className="w-full h-2 bg-slate-800 rounded overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${capped}%` }} />
    </div>
  );
}

function BreakdownRow({
  label,
  chars,
  percent,
  color,
}: {
  label: string;
  chars: number;
  percent: number;
  color: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className="text-slate-200">{label}</span>
        <span className="font-mono text-slate-300">
          {chars.toLocaleString()} chars · {percent.toFixed(1)}%
        </span>
      </div>
      <div className="w-full h-1.5 bg-slate-800 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  );
}

function percentColor(p: number): string {
  if (p >= 150) return 'text-red-400';
  if (p >= 100) return 'text-orange-400';
  if (p >= 80) return 'text-yellow-400';
  return 'text-emerald-400';
}
